/**
 * Locket second-photo cart reconciler (global, always-on, tiny).
 *
 * The second locket photo is a paid add-on line whose file and crop ride that
 * line (see blocks/mab-locket-upload.liquid + assets/mab-locket-upload.js). The
 * add-on line carries `_side: back` and a `_photo_pair_id` that matches the
 * locket line it belongs to. Deleting the add-on line removes the second photo
 * and its charge together, so that direction needs no code.
 *
 * The one case this file exists for: the customer removes (or reduces) the
 * LOCKET line but leaves the add-on, which would charge them for a second photo
 * of a locket they are not buying. On every cart change it caps each add-on
 * line's quantity to the quantity of the locket line sharing its pair id, and
 * removes any add-on whose locket is gone. It never ADDS (the photo file only
 * exists in the block on the product page), so it can only ever correct an
 * over-charge, which is the safe direction.
 *
 * Market locket prints lines are never touched. They come from the prints page,
 * carry their own `_Photo`, `_Photo2` and receipt, and belong to no
 * pair. Today they could not match anyway (they carry no `_side` and no
 * `_photo_pair_id`), so the skip below changes nothing on a live cart: it is
 * there so that a property name shared by accident later can never let this
 * file cap or remove a line the buyer sent her receipt with. The product is
 * named by handle, once, in Theme settings > Cart, and reaches this file as the
 * `data-prints-handle` attribute on its own script tag (snippets/scripts.liquid).
 *
 * Loaded once from snippets/scripts.liquid; guarded so it initializes once.
 * The guard holds `{ plan }`, the pure decision, so a check can run it against
 * a fixture cart without a network.
 */
(() => {
  if (window.__mabLocketReconcile) return;

  // A classic deferred script, so currentScript is this tag while the file runs.
  const script = document.currentScript;
  const PRINTS_HANDLE = ((script && script.dataset && script.dataset.printsHandle) || '').trim();

  const CHANGE_URL =
    (window.Theme && window.Theme.routes && window.Theme.routes.cart_change_url
      ? window.Theme.routes.cart_change_url + '.js'
      : '/cart/change.js');

  let inFlight = false;

  /**
   * The one correction this cart needs, or null when it is consistent.
   * Pure: reads the items, decides, touches nothing.
   *
   * @param {Array<{key: string, quantity: number, handle?: string, properties?: object}>} items
   * @param {string} printsHandle - the prints product's handle; blank skips nothing
   * @returns {{key: string, quantity: number} | null}
   */
  function plan(items, printsHandle) {
    // cart.js items carry the product handle as `handle`.
    const isPrints = (it) => Boolean(printsHandle) && it.handle === printsHandle;

    // Total quantity of locket (front) lines per pair id.
    const frontQty = new Map();
    for (const it of items) {
      if (isPrints(it)) continue;
      const props = it.properties || {};
      const pair = props._photo_pair_id;
      if (pair && props._side !== 'back') {
        frontQty.set(pair, (frontQty.get(pair) || 0) + it.quantity);
      }
    }

    // Each add-on line should not exceed its locket's quantity; none-left = remove.
    for (const it of items) {
      if (isPrints(it)) continue;
      const props = it.properties || {};
      if (props._side !== 'back' || !props._photo_pair_id) continue;
      const desired = frontQty.get(props._photo_pair_id) || 0;
      if (it.quantity > desired) return { key: it.key, quantity: desired };
    }
    return null;
  }

  window.__mabLocketReconcile = { plan };

  async function reconcile() {
    if (inFlight) return;
    inFlight = true;
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const fix = plan(cart.items || [], PRINTS_HANDLE);
      if (fix) {
        await change(fix.key, fix.quantity);
        // One correction per pass; the resulting cart:update re-runs us, and
        // an already-consistent cart is a no-op, so this converges.
        notify();
      }
    } catch (err) {
      // Never block the cart on a reconcile failure.
      console.error('[mab-locket-reconcile]', err);
    } finally {
      inFlight = false;
    }
  }

  function change(key, quantity) {
    return fetch(CHANGE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: key, quantity }),
    });
  }

  function notify() {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: { data: { source: 'mab-locket-reconcile' }, resource: {} },
      })
    );
  }

  // An add can never leave an add-on stranded. Only a locket line going away or
  // shrinking can, and that comes from the cart items component or the cart
  // page. Reading the whole cart after every add was a 215ms round trip on the
  // slowest screen in the shop, taken while the drawer was trying to paint, and
  // it could never find anything to correct.
  const NO_ORPHAN_SOURCES = ['mab-locket-reconcile', 'product-form-component', 'mab-locket-addon'];

  document.addEventListener('cart:update', (e) => {
    const source = e.detail && e.detail.data && e.detail.data.source;
    // The first of these is the event we dispatch ourselves, so we don't spin.
    if (NO_ORPHAN_SOURCES.includes(source)) return;
    reconcile();
  });
})();
